import React from 'react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
    AvailableFields,
    LeadFieldMapping,
    NO_MAPPING_VALUE,
    isTargetAlreadyMapped,
} from './mappingValidation';

/**
 * A single source field offered on the left-hand side of the mapping table.
 * For the CSV import wizard this is a column header; for the campaign editor
 * (W3) this will be a Facebook form question or a landing page payload key.
 */
export interface MappingSourceField {
    key: string;
    label?: string;
    sample?: unknown;
    options?: Array<{ key: string; label: string }> | null;
}

export interface LeadFieldMappingTableProps {
    /** Left-hand column: the fields available to map from. */
    sourceFields: MappingSourceField[];
    /** Right-hand column: the CRM lead fields a source field can map to. */
    availableFields: AvailableFields;
    /** Current mapping, keyed by source field key. */
    mapping: LeadFieldMapping;
    /** Called with the resolved target (null when "No mapping" is chosen). */
    onChange: (sourceKey: string, target: string | null) => void;
    /** Suggested mapping per source key, used to show the "Suggested" badge. */
    suggestedMappings?: Record<string, string | null>;
    /**
     * Optional sample rows (keyed by source field key) rendered below the
     * mapping selects, e.g. the import wizard's CSV preview rows.
     */
    previewRows?: Record<string, unknown>[];
    /**
     * Targets allowed to be mapped from more than one source field (e.g.
     * `name`, `notes` once the campaign editor's combine UI lands in W3).
     * Defaults to none, matching the import wizard's "one target, one
     * source" rule.
     */
    combinableTargets?: string[];
}

const LeadFieldMappingTable: React.FC<LeadFieldMappingTableProps> = ({
    sourceFields,
    availableFields,
    mapping,
    onChange,
    suggestedMappings = {},
    previewRows = [],
    combinableTargets = [],
}) => {
    const handleSelectChange = (sourceKey: string, value: string) => {
        onChange(sourceKey, value === NO_MAPPING_VALUE ? null : value);
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="border-b">
                        {sourceFields.map(field => {
                            const rawValue = mapping[field.key];
                            const currentValue = rawValue || NO_MAPPING_VALUE;
                            const isMapped = currentValue !== NO_MAPPING_VALUE;

                            const otherSourcesWithSameTarget = isMapped
                                ? Object.entries(mapping).filter(
                                      ([sourceKey, target]) =>
                                          sourceKey !== field.key && target === rawValue
                                  )
                                : [];

                            const isSuggested =
                                !!suggestedMappings[field.key] &&
                                suggestedMappings[field.key] === rawValue &&
                                isMapped;

                            return (
                                <th
                                    key={field.key}
                                    className="text-left p-2 font-medium bg-muted/50 min-w-[200px]"
                                >
                                    <div className="space-y-2">
                                        <div className="space-y-2">
                                            <Badge variant="outline" className="text-xs">
                                                {field.label ?? field.key}
                                            </Badge>

                                            <div className="space-y-1">
                                                <Select
                                                    value={currentValue}
                                                    onValueChange={value =>
                                                        handleSelectChange(field.key, value)
                                                    }
                                                >
                                                    <SelectTrigger className="h-8 text-xs">
                                                        <SelectValue placeholder="Map to field..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value={NO_MAPPING_VALUE}>
                                                            No mapping
                                                        </SelectItem>
                                                        {Object.entries(availableFields).map(
                                                            ([fieldName, fieldConfig]) => {
                                                                const isAlreadyMapped =
                                                                    isTargetAlreadyMapped(
                                                                        mapping,
                                                                        fieldName,
                                                                        field.key,
                                                                        combinableTargets
                                                                    );
                                                                return (
                                                                    <SelectItem
                                                                        key={fieldName}
                                                                        value={fieldName}
                                                                        disabled={isAlreadyMapped}
                                                                        className={
                                                                            isAlreadyMapped
                                                                                ? 'opacity-50 cursor-not-allowed'
                                                                                : ''
                                                                        }
                                                                    >
                                                                        <span className="text-xs">
                                                                            {fieldConfig.label}
                                                                            {fieldConfig.required && (
                                                                                <span className="text-red-500 ml-1">
                                                                                    *
                                                                                </span>
                                                                            )}
                                                                            {isAlreadyMapped && (
                                                                                <span className="text-muted-foreground ml-1">
                                                                                    (already mapped)
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </SelectItem>
                                                                );
                                                            }
                                                        )}
                                                    </SelectContent>
                                                </Select>

                                                {otherSourcesWithSameTarget.length > 0 && (
                                                    <div className="text-xs text-orange-600 dark:text-orange-400 bg-orange-500/10 px-2 py-1 rounded">
                                                        ⚠️ Also mapped to:{' '}
                                                        {otherSourcesWithSameTarget
                                                            .map(([sourceKey]) => sourceKey)
                                                            .join(', ')}
                                                    </div>
                                                )}

                                                {isSuggested && (
                                                    <Badge variant="secondary" className="text-xs">
                                                        Suggested
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                {previewRows.length > 0 && (
                    <tbody>
                        {previewRows.map((row, rowIndex) => (
                            <tr key={rowIndex} className="border-b hover:bg-muted/50">
                                {sourceFields.map(field => (
                                    <td key={field.key} className="p-2 text-xs">
                                        {(row[field.key] as React.ReactNode) || '-'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                )}
            </table>
        </div>
    );
};

export default LeadFieldMappingTable;
